import { Component, type ReactNode } from 'react';

interface Props {
  /** Changes when instance props change — clears a captured error so a fix recovers. */
  propsKey: string;
  children: ReactNode;
}
interface State {
  error: Error | null;
}

/**
 * Isolates a frame's TSX render. A runtime throw inside the frame's component
 * (e.g. referencing an un-imported symbol) would otherwise bubble to the app
 * root and crash the whole canvas. This catches it and shows the message inline.
 *
 * Recovery: the parent keys this boundary by the tsx source, so editing the
 * source remounts it fresh. `propsKey` clears a captured error when only the
 * instance props change (no remount, so component state is preserved).
 */
export class TsxErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.propsKey !== this.props.propsKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (this.state.error) {
      return (
        <div className="cf-missing" data-cf-error="runtime">
          {this.state.error.message || String(this.state.error)}
        </div>
      );
    }
    return this.props.children;
  }
}
